import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table, Tabs, Tab } from 'react-bootstrap';
import { documentService, employeeService } from '../services/apiService.jsx';
import Layout from '../components/Layout.jsx';
import './Documents.css';

const KRA_PREFIX = 'KRA:';
const MANUAL_DEPARTMENT = 'BTS User Manual';

const getEmployeeName = (employee) =>
  employee?.name || [employee?.first_name, employee?.last_name].filter(Boolean).join(' ') || `Employee ${employee?.employee_id}`;

const isAdminRole = (role) => ['admin', 'super_admin', 'super admin'].includes(String(role || '').toLowerCase().trim());

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeTab, setActiveTab] = useState('manuals');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [uploadForm, setUploadForm] = useState({ document_title: '', document_description: '', documents: null });

  const manuals = useMemo(() => documents.filter((doc) => !String(doc.department || '').startsWith(KRA_PREFIX)), [documents]);
  const kraDocuments = useMemo(() => documents.filter((doc) => String(doc.department || '').startsWith(KRA_PREFIX)), [documents]);
  const employeeOptions = useMemo(() => employees.filter((employee) => !isAdminRole(employee.role)), [employees]);
  const employeesById = useMemo(() => new Map(employeeOptions.map((employee) => [String(employee.employee_id), employee])), [employeeOptions]);
  const selectedEmployee = employeeOptions.find((employee) => String(employee.employee_id) === String(selectedEmployeeId));
  const employeeKras = selectedEmployeeId === 'all'
    ? kraDocuments.filter((doc) => employeesById.has(String(doc.department).slice(KRA_PREFIX.length)))
    : kraDocuments.filter((doc) => String(doc.department).slice(KRA_PREFIX.length) === String(selectedEmployeeId));

  const fetchDocuments = async () => {
    try {
      const data = await documentService.fetchDocuments();
      const items = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
      setDocuments(items);
      setError('');
    } catch (err) {
      setError('Could not load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
    employeeService.getAllEmployees().then((response) => {
      const list = Array.isArray(response?.data) ? response.data : response?.data?.data;
      setEmployees(Array.isArray(list) ? list : []);
    }).catch(() => setError('Could not load employees for the KRA section.'));
  }, []);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const openUpload = (kind) => {
    setStatus('');
    setUploadForm({ document_title: kind === 'kra' && selectedEmployee ? `KRA - ${getEmployeeName(selectedEmployee)}` : '', document_description: '', documents: null });
    setShowUpload(true);
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadForm.documents) return;
    const formData = new FormData();
    formData.append('department', activeTab === 'kra' ? `${KRA_PREFIX}${selectedEmployeeId}` : MANUAL_DEPARTMENT);
    formData.append('document_title', uploadForm.document_title);
    formData.append('document_description', uploadForm.document_description || (activeTab === 'kra' ? `KRA for ${getEmployeeName(selectedEmployee)}` : 'BTS user manual'));
    formData.append('documents', uploadForm.documents);
    try {
      await documentService.uploadDocument(formData);
      setShowUpload(false);
      setStatus(activeTab === 'kra' ? 'KRA PDF uploaded.' : 'User manual uploaded.');
      await fetchDocuments();
    } catch (err) {
      setStatus('Upload failed. Please try again.');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete “${doc.document_title}”?`)) return;
    try {
      await documentService.deleteDocument(doc.doc_id);
      setStatus('Document deleted.');
      await fetchDocuments();
    } catch (err) {
      setStatus('Could not delete this document.');
    }
  };

  const handlePreview = async (doc) => {
    setSelectedDoc(doc);
    setPreviewUrl('');
    setPreviewError('');
    setPreviewLoading(true);
    try {
      const response = await documentService.fetchDocumentFile(doc.doc_id);
      setPreviewUrl(URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })));
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Could not open this PDF. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentService.fetchDocumentFile(doc.doc_id);
      const fileUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = `${doc.document_title || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    } catch (err) {
      setStatus(err.response?.data?.message || 'Could not download this PDF.');
    }
  };

  const renderTable = (items, emptyText, showEmployee = false) => (
    <div className="documents-table-wrap">
      <Table hover responsive className="documents-table mb-0">
        <thead><tr>{showEmployee && <th>Employee</th>}<th>Document</th><th>Description</th><th>Added by</th><th className="text-end">Actions</th></tr></thead>
        <tbody>
          {items.map((doc) => (
            <tr key={doc.doc_id}>
              {showEmployee && <td>{getEmployeeName(employeesById.get(String(doc.department).slice(KRA_PREFIX.length)) || { employee_id: String(doc.department).slice(KRA_PREFIX.length) })}</td>}
              <td><div className="document-title"><i className="bi bi-file-earmark-pdf-fill" />{doc.document_title}</div></td>
              <td>{doc.documnets_descrtiption || doc.document_description || '—'}</td>
              <td>{doc.emp_name || '—'}</td>
              <td><div className="document-actions">
                <Button size="sm" variant="outline-primary" onClick={() => handlePreview(doc)}><i className="bi bi-eye me-1" />View</Button>
                <Button size="sm" variant="primary" onClick={() => handleDownload(doc)}><i className="bi bi-download me-1" />Download</Button>
                <Button size="sm" variant="outline-danger" aria-label={`Delete ${doc.document_title}`} onClick={() => handleDelete(doc)}><i className="bi bi-trash" /></Button>
              </div></td>
            </tr>
          ))}
          {!loading && items.length === 0 && <tr><td colSpan={showEmployee ? 5 : 4} className="documents-empty">{emptyText}</td></tr>}
        </tbody>
      </Table>
    </div>
  );

  return (
    <Layout>
      <main className="documents-page">
        <header className="documents-header">
          <div><p className="documents-eyebrow">BTS WORKSPACE</p><h1>Documents</h1><p>Find user guides and employee KRA documents in one place.</p></div>
          <div className="documents-header-icon"><i className="bi bi-folder2-open" /></div>
        </header>
        {error && <Alert variant="warning" dismissible onClose={() => setError('')}>{error}</Alert>}
        {status && <Alert variant={status.includes('failed') || status.includes('Could not') ? 'danger' : 'success'} dismissible onClose={() => setStatus('')}>{status}</Alert>}
        <Tabs activeKey={activeTab} onSelect={(key) => { setActiveTab(key); setStatus(''); }} className="documents-tabs mb-4">
          <Tab eventKey="manuals" title={<><i className="bi bi-journal-bookmark me-2" />BTS User Manuals</>}>
            <Card className="documents-card">
              <Card.Body>
                <div className="documents-section-heading"><div><h2>User manuals</h2><p>PDF guides for using the BTS system.</p></div><Button onClick={() => openUpload('manual')}><i className="bi bi-plus-lg me-2" />Add manual</Button></div>
                {renderTable(manuals, 'No user manuals have been added yet.')}
              </Card.Body>
            </Card>
          </Tab>
          <Tab eventKey="kra" title={<><i className="bi bi-person-vcard me-2" />Employee KRA</>}>
            <Card className="documents-card">
              <Card.Body>
                <div className="documents-section-heading"><div><h2>Employee KRA</h2><p>Select an employee to view or add their KRA PDF.</p></div></div>
                <div className="kra-employee-select">
                  <Form.Group controlId="kra-employee"><Form.Label>Employee</Form.Label><Form.Select value={selectedEmployeeId} onChange={(event) => setSelectedEmployeeId(event.target.value)}>
                    <option value="">Choose an employee</option>
                    <option value="all">All employees</option>
                    {employeeOptions.map((employee) => <option value={employee.employee_id} key={employee.employee_id}>{getEmployeeName(employee)}{employee.department_name ? ` · ${employee.department_name}` : ''}</option>)}
                  </Form.Select></Form.Group>
                  {selectedEmployee && <Button onClick={() => openUpload('kra')}><i className="bi bi-plus-lg me-2" />Add KRA PDF</Button>}
                </div>
                {selectedEmployeeId === 'all' ? <>
                  <div className="kra-employee-summary"><span className="kra-avatar"><i className="bi bi-people-fill" /></span><div><strong>All employees</strong><span>{employeeKras.length} KRA {employeeKras.length === 1 ? 'document' : 'documents'}</span></div></div>
                  {renderTable(employeeKras, 'No KRA PDFs have been added yet.', true)}
                </> : selectedEmployee ? <>
                  <div className="kra-employee-summary"><span className="kra-avatar"><i className="bi bi-person-fill" /></span><div><strong>{getEmployeeName(selectedEmployee)}</strong><span>{selectedEmployee.department_name || 'Employee'}{selectedEmployee.employee_code ? ` · ${selectedEmployee.employee_code}` : ''}</span></div></div>
                  {renderTable(employeeKras, 'No KRA PDF has been added for this employee.')}
                </> : <div className="documents-empty kra-prompt"><i className="bi bi-person-search"/><span>Select an employee to see their KRA documents.</span></div>}
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </main>

      <Modal show={showUpload} onHide={() => setShowUpload(false)} centered>
        <Modal.Header closeButton><Modal.Title>{activeTab === 'kra' ? 'Add employee KRA' : 'Add BTS user manual'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleUpload}>
          <Modal.Body>
            {activeTab === 'kra' && selectedEmployee && <p className="text-muted">For {getEmployeeName(selectedEmployee)}</p>}
            <Form.Group className="mb-3"><Form.Label>Title</Form.Label><Form.Control required value={uploadForm.document_title} onChange={(event) => setUploadForm({ ...uploadForm, document_title: event.target.value })} placeholder={activeTab === 'kra' ? 'e.g. FY 2026–27 KRA' : 'e.g. Task Management Guide'} /></Form.Group>
            <Form.Group className="mb-3"><Form.Label>Description <span className="text-muted">(optional)</span></Form.Label><Form.Control as="textarea" rows={2} value={uploadForm.document_description} onChange={(event) => setUploadForm({ ...uploadForm, document_description: event.target.value })} /></Form.Group>
            <Form.Group><Form.Label>PDF file</Form.Label><Form.Control required type="file" accept="application/pdf,.pdf" onChange={(event) => setUploadForm({ ...uploadForm, documents: event.target.files?.[0] || null })} /></Form.Group>
          </Modal.Body>
          <Modal.Footer><Button variant="light" onClick={() => setShowUpload(false)}>Cancel</Button><Button type="submit">Upload PDF</Button></Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(selectedDoc)} onHide={() => setSelectedDoc(null)} size="xl" centered>
        <Modal.Header closeButton><Modal.Title>{selectedDoc?.document_title}</Modal.Title></Modal.Header>
        <Modal.Body className="document-preview-body">
          {previewLoading && <div className="document-preview-message">Loading PDF…</div>}
          {previewError && <div className="document-preview-message text-danger">{previewError}</div>}
          {!previewLoading && !previewError && previewUrl && <iframe title={`Preview ${selectedDoc?.document_title}`} src={previewUrl} />}
        </Modal.Body>
        <Modal.Footer><Button disabled={!previewUrl} onClick={() => selectedDoc && handleDownload(selectedDoc)}><i className="bi bi-download me-2" />Download PDF</Button></Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default Documents;
